import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { MessageCircle, Send, Brain, PencilLine, Sparkles, ThumbsUp, ThumbsDown, Lock as LockIcon, Crown, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from './ui/dialog';
import { AIAssistant } from '../lib/ai-assistant';
import { analytics } from '../lib/analytics';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth-context';
import { useSubscriptionPlan } from '../lib/use-subscription-plan';
import { buildTensionUserSnapshotForAtlas } from '../lib/tension-atlas-user-snapshot';
import { projectId } from '/utils/supabase/info';
import { getAuthHeadersForProtectedApi } from '../lib/db-storage';

interface CopilotMessage {
  role: 'user' | 'assistant';
  content: string;
  prompt?: string;
  latencyMs?: number;
  mode?: CopilotMode;
}

type CopilotContext = 'home' | 'settings' | 'support' | 'app';
const COPILOT_SESSION_KEY = 'tension_consumer_copilot_messages_v1';
const COPILOT_PERSONA_KEY = 'tension_consumer_copilot_persona_v1';
const COPILOT_MODE_KEY = 'tension_consumer_copilot_mode_v1';
type CopilotPersona = 'coach' | 'planner' | 'crisis';
type CopilotMode = 'atlas_pro' | 'live_companion';

type LiveCompanionOutput = {
  sendNow: string;
  softerVersion: string;
  boundaryVersion: string;
  ifTheyPushBack: string;
  whyThisWorks: string;
} | null;

function parseLiveCompanionOutput(content: string): LiveCompanionOutput {
  const text = String(content || '').trim();
  if (!text || !/LIVE_COMPANION/i.test(text)) return null;
  const pick = (label: string) => {
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const rx = new RegExp(`${escaped}:\\s*([\\s\\S]*?)(?=\\n[A-Z_]+:\\s|$)`, 'i');
    const m = text.match(rx);
    return m?.[1]?.trim() || '';
  };
  const parsed = {
    sendNow: pick('SEND_NOW'),
    softerVersion: pick('SOFTER_VERSION'),
    boundaryVersion: pick('BOUNDARY_VERSION'),
    ifTheyPushBack: pick('IF_THEY_PUSH_BACK'),
    whyThisWorks: pick('WHY_THIS_WORKS'),
  };
  if (!parsed.sendNow && !parsed.softerVersion && !parsed.boundaryVersion) return null;
  return parsed;
}

export function ConsumerAICopilot({ context = 'home' }: { context?: CopilotContext }) {
  const navigate = useNavigate();
  const auth = useAuth();
  const { hasTensionAI, loading: subLoading, plan } = useSubscriptionPlan();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [displayContent, setDisplayContent] = useState('');
  const [steps, setSteps] = useState<Array<{ id: string; label: string; status: 'pending' | 'active' | 'done' }>>([]);
  const [persona, setPersona] = useState<CopilotPersona>('coach');
  const [copilotMode, setCopilotMode] = useState<CopilotMode>('atlas_pro');
  const [feedbackByMessage, setFeedbackByMessage] = useState<Record<number, 'up' | 'down'>>({});
  const [outcomeByMessage, setOutcomeByMessage] = useState<Record<number, 'resolved' | 'improved' | 'not_helpful'>>({});
  const [fabVisible, setFabVisible] = useState(false);
  const [modePreferenceReady, setModePreferenceReady] = useState(false);
  const modeDescription = copilotMode === 'live_companion'
    ? 'Instant message engine: send-now, softer, boundary, and pushback-ready lines.'
    : 'Deep strategic intelligence: pattern analysis, reflection, and relationship planning.';

  const canSend = input.trim().length > 0 && !loading;
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(COPILOT_SESSION_KEY);
      if (!saved) return;
      const parsed = JSON.parse(saved);
      if (!Array.isArray(parsed)) return;
      const normalized = parsed.filter(
        (m: any) => m && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string',
      );
      setMessages(normalized.slice(-40));
    } catch {
      // ignore corrupt session data
    }
  }, []);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(COPILOT_PERSONA_KEY);
      if (saved === 'coach' || saved === 'planner' || saved === 'crisis') {
        setPersona(saved);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(COPILOT_MODE_KEY);
      if (saved === 'atlas_pro' || saved === 'live_companion') {
        setCopilotMode(saved);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const signedIn = !!auth?.user;
    const token = auth?.accessToken;
    if (!signedIn || !hasTensionAI || !token) {
      setModePreferenceReady(true);
      return () => {
        cancelled = true;
      };
    }
    const loadPreference = async () => {
      try {
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/ai/copilot/preferences`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          },
        );
        const data = await response.json().catch(() => null);
        const mode = data?.preferences?.mode;
        if (!cancelled && (mode === 'atlas_pro' || mode === 'live_companion')) {
          setCopilotMode(mode);
          try {
            sessionStorage.setItem(COPILOT_MODE_KEY, mode);
          } catch {
            // ignore storage failure
          }
        }
      } catch {
        // non-blocking; keep local mode
      } finally {
        if (!cancelled) setModePreferenceReady(true);
      }
    };
    void loadPreference();
    return () => {
      cancelled = true;
    };
  }, [auth?.user, auth?.accessToken, hasTensionAI]);

  useEffect(() => {
    try {
      sessionStorage.setItem(COPILOT_SESSION_KEY, JSON.stringify(messages.slice(-40)));
    } catch {
      // ignore storage failures
    }
  }, [messages]);

  useEffect(() => {
    try {
      sessionStorage.setItem(COPILOT_PERSONA_KEY, persona);
      analytics.track('copilot_persona_selected', { persona, context });
    } catch {
      // ignore
    }
  }, [persona, context]);

  useEffect(() => {
    try {
      sessionStorage.setItem(COPILOT_MODE_KEY, copilotMode);
      analytics.track('copilot_mode_selected', { mode: copilotMode, context });
    } catch {
      // ignore
    }
  }, [copilotMode, context]);

  useEffect(() => {
    const signedIn = !!auth?.user;
    const token = auth?.accessToken;
    if (!modePreferenceReady || !signedIn || !hasTensionAI || !token) return;
    const timer = window.setTimeout(() => {
      void fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/ai/copilot/preferences`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            mode: copilotMode,
          }),
        },
      ).catch(() => undefined);
    }, 220);
    return () => window.clearTimeout(timer);
  }, [copilotMode, auth?.user, auth?.accessToken, hasTensionAI, modePreferenceReady]);

  useEffect(() => {
    let cancelled = false;
    const reducedMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const delayMs = reducedMotion ? 0 : 260;
    const timer = window.setTimeout(() => {
      if (!cancelled) setFabVisible(true);
    }, delayMs);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, []);

  const baseSuggestions = useMemo(() => {
    if (context === 'settings') {
      return [
        'Help me optimize my notification settings for consistency.',
        'What settings should I change to reduce anxiety triggers?',
        'Give me a weekly account health checklist.',
      ];
    }
    if (context === 'support') {
      return [
        'Help me write a clear support message with all key details.',
        'Summarize my issue so support can resolve it quickly.',
        'What troubleshooting steps should I try before submitting?',
      ];
    }
    if (context === 'app') {
      return [
        'What should I focus on today based on my recent check-ins?',
        'Help me interpret my tension trend on this screen.',
        'Give me one concrete next step for relationship repair.',
      ];
    }
    return [
      'Analyze my check-in patterns and tension trends — what stands out?',
      'What should I focus on today based on my current state?',
      'Help me write a calm message to my partner.',
      'Micro-coach this draft: "Why are you ignoring me?"',
      'Give me a 24-hour plan to lower tension.',
    ];
  }, [context]);

  const suggestions = useMemo(() => {
    if (copilotMode === 'live_companion') {
      return [
        'I need a send-now reply in my voice for this situation.',
        'Give me 2 branching replies depending on how they respond next.',
        'Turn this heated draft into calm, high-impact language.',
      ];
    }
    if (persona === 'planner') {
      return [
        'Create a 3-step plan for today and tomorrow.',
        'Turn my situation into a checklist with timings.',
        'Give me a risk/priority matrix for next actions.',
      ];
    }
    if (persona === 'crisis') {
      return [
        'I feel overwhelmed right now. Give me immediate grounding steps.',
        'Help me de-escalate before I send a message.',
        'Give me a safe 30-minute stabilization plan.',
      ];
    }
    return baseSuggestions;
  }, [copilotMode, persona, baseSuggestions]);

  const personaInstruction = useMemo(() => {
    if (copilotMode === 'live_companion') {
      return 'Mode: Live Companion. Act like a real-time relationship intelligence copilot. Be dynamic, specific, non-repetitive, and input-anchored. Prioritize immediate send-ready guidance, likely partner reaction branches, and concise next steps. Avoid canned empathy templates.';
    }
    if (persona === 'planner') {
      return 'Persona: Planner. Respond with structured, step-by-step plans, concise checklists, and concrete next actions with timelines.';
    }
    if (persona === 'crisis') {
      return 'Persona: Crisis Support. Use calm, grounding language, de-escalation guidance, safety-first framing, and immediate practical steps. Always start with a 3-step immediate stabilization protocol. Include a short "If you are in danger now, call local emergency services immediately" line. Avoid judgmental language.';
    }
    return 'Persona: Coach. Use supportive tone, reflective guidance, and practical behavior suggestions balanced with encouragement.';
  }, [copilotMode, persona]);

  const microCoachInstruction =
    'Micro-Coach mode: evaluate message escalation risk and produce a safer rewrite. Return: (1) risk level, (2) why it may escalate/de-escalate, (3) rewritten message in the same voice, (4) short send-now version, (5) calmer wait-two-hours version.';

  const setActiveStep = (id: string) => {
    setSteps((prev) =>
      prev.map((step) => {
        if (step.id === id) return { ...step, status: 'active' };
        if (step.status === 'active') return { ...step, status: 'done' };
        return step;
      }),
    );
  };

  const surfaceForMode = (mode?: CopilotMode): 'consumer_chat' | 'atlas_live_companion' =>
    mode === 'live_companion' ? 'atlas_live_companion' : 'consumer_chat';

  const copyText = async (value: string) => {
    const text = String(value || '').trim();
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const submitQualityFeedback = async (
    rating: 'up' | 'down',
    surface: 'consumer_chat' | 'atlas_live_companion',
    response: string,
    prompt: string,
    latencyMs?: number,
  ) => {
    try {
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/ai/quality/feedback`,
        {
          method: 'POST',
          headers: getAuthHeadersForProtectedApi(),
          body: JSON.stringify({
            rating,
            surface,
            persona,
            outcome: rating === 'up' ? 'helpful' : 'needs_work',
            prompt,
            response,
            context,
            latencyMs: Number.isFinite(Number(latencyMs)) ? Number(latencyMs) : null,
          }),
        },
      );
    } catch {
      // Non-blocking telemetry path.
    }
  };

  const submitOutcomeFeedback = async (
    outcome: 'resolved' | 'improved' | 'not_helpful',
    surface: 'consumer_chat' | 'atlas_live_companion',
    response: string,
    prompt: string,
    latencyMs?: number,
  ) => {
    try {
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/ai/quality/outcome`,
        {
          method: 'POST',
          headers: getAuthHeadersForProtectedApi(),
          body: JSON.stringify({
            outcome,
            surface,
            persona,
            prompt,
            response,
            context,
            latencyMs: Number.isFinite(Number(latencyMs)) ? Number(latencyMs) : null,
          }),
        },
      );
    } catch {
      // Non-blocking telemetry path.
    }
  };

  const runCopilot = async () => {
    const prompt = input.trim();
    if (!prompt || loading) return;

    setMessages((prev) => [...prev, { role: 'user', content: prompt, mode: copilotMode }]);
    analytics.track('copilot_prompt_submitted', {
      context,
      persona,
      mode: copilotMode,
      promptLength: prompt.length,
      historySize: messages.length,
    });
    setInput('');
    setLoading(true);
    setDisplayContent('');
    const startedAt = Date.now();
    setSteps(
      copilotMode === 'live_companion'
        ? [
            { id: 'understand', label: 'Reading your exact wording', status: 'active' },
            { id: 'context', label: 'Mapping likely reaction paths', status: 'pending' },
            { id: 'draft', label: 'Generating send-ready variants', status: 'pending' },
            { id: 'polish', label: 'Tightening tone and clarity', status: 'pending' },
          ]
        : [
            { id: 'understand', label: 'Understanding your request', status: 'active' },
            { id: 'context', label: 'Reviewing relevant context', status: 'pending' },
            { id: 'draft', label: 'Drafting response', status: 'pending' },
            { id: 'polish', label: 'Polishing answer', status: 'pending' },
          ],
    );

    try {
      setActiveStep('context');
      const microCoachRequested =
        /micro[-\s]?coach|rewrite this|message draft|help me send this|text this/i.test(prompt);
      const userTensionSnapshot = buildTensionUserSnapshotForAtlas();
      let streamedText = '';
      const result = await AIAssistant.streamMessage(
        `${personaInstruction}${microCoachRequested ? `\n${microCoachInstruction}` : ''}\n\nUser request: ${prompt}`,
        {
          includeCodeContext: false,
          includeWebSearch: false,
          userTensionSnapshot,
          aiMode: copilotMode,
          aiSurface: copilotMode === 'live_companion' ? 'atlas_live_companion' : 'consumer_chat',
          onDelta: (delta) => {
            streamedText += delta;
            setDisplayContent(streamedText);
          },
        },
      );
      setActiveStep('draft');

      const text = result.content || streamedText;
      if (!text) throw new Error('No response generated');
      const latencyMs = Date.now() - startedAt;

      analytics.track('copilot_response_received', {
        context,
        persona,
        mode: copilotMode,
        latencyMs,
        responseLength: text.length,
      });

      setActiveStep('polish');
      setSteps((prev) => prev.map((s) => ({ ...s, status: 'done' })));
      setMessages((prev) => [...prev, { role: 'assistant', content: text, prompt, latencyMs, mode: copilotMode }]);
      setDisplayContent('');
    } catch (error) {
      console.error('Consumer copilot error:', error);
      analytics.track('copilot_request_failed', {
        context,
        persona,
        mode: copilotMode,
        latencyMs: Date.now() - startedAt,
        error: error instanceof Error ? error.message : String(error),
      });
      toast.error(error instanceof Error ? error.message : 'AI request failed');
    } finally {
      setLoading(false);
    }
  };

  const signedIn = !!auth?.user;
  const atlasLocked = signedIn && !hasTensionAI;
  const modeLabel = copilotMode === 'live_companion' ? 'Live Companion' : 'ATLAS Pro';

  return (
    <div
      className={`fixed z-[70] right-3 bottom-24 md:right-4 md:bottom-4 lg:right-6 transition-all duration-300 ease-out ${
        fabVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
      }`}
    >
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button
            aria-label={atlasLocked ? 'Open Atlas Pro upgrade dialog' : 'Open TENSION Atlas'}
            className={`rounded-full border transition-all shadow-xl ${
              atlasLocked
                ? 'bg-gradient-to-r from-neutral-700 to-neutral-800 hover:from-neutral-600 hover:to-neutral-700 border-neutral-500/40 text-white'
                : 'bg-gradient-to-r from-violet-600 via-fuchsia-600 to-purple-600 hover:from-violet-500 hover:via-fuchsia-500 hover:to-purple-500 border-violet-300/40 text-white'
            }`}
            onClick={() => analytics.track('copilot_opened', { context, plan, locked: atlasLocked })}
          >
            <span className="inline-flex md:hidden h-11 w-11 items-center justify-center">
              {atlasLocked ? (
                <LockIcon className="w-5 h-5 shrink-0" />
              ) : (
                <Crown className="w-5 h-5 text-amber-200 shrink-0" />
              )}
            </span>
            <span className="hidden md:inline-flex items-center h-12 px-5 text-sm lg:text-base">
              {atlasLocked ? (
                <LockIcon className="w-4 h-4 mr-2 shrink-0" />
              ) : (
                <Crown className="w-4 h-4 mr-2 text-amber-200 shrink-0" />
              )}
              {atlasLocked ? 'Atlas (Pro)' : 'TENSION Atlas Pro'}
            </span>
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-2xl p-0 overflow-hidden border-neutral-700 bg-neutral-900 text-white">
          <DialogHeader className="p-4 border-b border-neutral-800">
            <DialogTitle className="text-white">TENSION Atlas</DialogTitle>
            <DialogDescription className="text-zinc-200">
              1:1 relationship guidance — private, practical, and focused on you. Uses your saved check-ins when you ask for patterns or trends.
            </DialogDescription>
          </DialogHeader>

          {!signedIn && (
            <div className="p-8 text-center space-y-4">
              <LockIcon className="w-10 h-10 mx-auto text-violet-400" />
              <p className="text-neutral-300">Sign in to use TENSION Atlas.</p>
              <Button onClick={() => { setOpen(false); navigate('/auth'); }} className="bg-violet-600">
                Sign in
              </Button>
            </div>
          )}

          {signedIn && atlasLocked && !subLoading && (
            <div className="p-8 text-center space-y-4">
              <Crown className="w-12 h-12 mx-auto text-amber-400" />
              <p className="text-lg text-white font-medium">Atlas is a PRO feature</p>
              <p className="text-sm text-neutral-400 max-w-md mx-auto">
                Upgrade for secure 1:1 coaching inside TENSION — relationship dynamics, scripts, and calm next steps.
                Your plan: <span className="text-violet-300 uppercase">{plan}</span>
              </p>
              <Button
                onClick={() => {
                  setOpen(false);
                  navigate('/pricing');
                }}
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
              >
                Upgrade to PRO
              </Button>
            </div>
          )}

          {signedIn && subLoading && (
            <div className="p-8 text-center text-neutral-400 text-sm">Loading your plan…</div>
          )}

          {signedIn && hasTensionAI && !subLoading && (
          <>
          <div className="px-4 pt-3 pb-0">
            <div className="rounded-lg border border-neutral-800 bg-neutral-900/70 p-2">
              <div className="mb-2 flex items-center justify-between">
                <p className="text-[11px] uppercase tracking-wide text-neutral-400">Feature Select</p>
                <p className="text-[11px] text-violet-300">{modeLabel}</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'atlas_pro', label: 'ATLAS Pro' },
                  { id: 'live_companion', label: 'Live Companion' },
                ].map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => setCopilotMode(mode.id as CopilotMode)}
                    className={`rounded-md border px-2 py-1.5 text-xs transition-colors ${
                      copilotMode === mode.id
                        ? 'border-violet-500 bg-violet-500/20 text-violet-200'
                        : 'border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                    }`}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-[11px] text-neutral-400">{modeDescription}</p>
            </div>
          </div>
          <div className="p-4 space-y-4 max-h-[68vh] overflow-y-auto">
            {messages.length === 0 && (
              <div className="space-y-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => setInput(s)}
                    className="w-full text-left text-sm rounded-md border border-neutral-700 bg-neutral-800 px-3 py-2 hover:bg-neutral-700"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {copilotMode === 'atlas_pro' && persona === 'crisis' && (
              <div className="tension-glow rounded-lg border border-red-700/50 bg-red-950/30 p-3">
                <p className="text-xs uppercase tracking-wide text-red-300 mb-1">Crisis Support Priority Mode</p>
                <p className="text-sm text-red-100">
                  Focus: immediate de-escalation, grounding, and safety-first guidance.
                  If there is risk of harm, contact local emergency services now.
                </p>
              </div>
            )}

            {messages.map((msg, idx) => (
              <div key={idx} className={`tension-glow rounded-lg p-3 ${msg.role === 'user' ? 'bg-violet-900/30 border border-violet-800' : 'bg-neutral-800 border border-neutral-700'}`}>
                <p className="text-xs text-neutral-400 mb-1">{msg.role === 'user' ? 'You' : 'AI'}</p>
                {msg.role === 'assistant' && msg.mode === 'live_companion' ? (
                  (() => {
                    const parsed = parseLiveCompanionOutput(msg.content);
                    if (!parsed) return <p className="text-sm whitespace-pre-wrap">{msg.content}</p>;
                    return (
                      <div className="space-y-2">
                        {[
                          { key: 'sendNow', label: 'Send now', value: parsed.sendNow },
                          { key: 'softerVersion', label: 'Softer version', value: parsed.softerVersion },
                          { key: 'boundaryVersion', label: 'Boundary version', value: parsed.boundaryVersion },
                          { key: 'ifTheyPushBack', label: 'If they push back', value: parsed.ifTheyPushBack },
                        ].map((row) => (
                          <div key={row.key} className="rounded-md border border-neutral-700 bg-neutral-900/60 p-2">
                            <div className="mb-1 flex items-center justify-between gap-2">
                              <p className="text-[11px] uppercase tracking-wide text-neutral-400">{row.label}</p>
                              <button
                                type="button"
                                onClick={() => void copyText(row.value)}
                                className="text-[11px] text-violet-300 hover:text-violet-200"
                              >
                                Copy
                              </button>
                            </div>
                            <p className="text-sm whitespace-pre-wrap">{row.value || '-'}</p>
                          </div>
                        ))}
                        {parsed.whyThisWorks ? (
                          <p className="text-xs text-neutral-400">Why this works: {parsed.whyThisWorks}</p>
                        ) : null}
                      </div>
                    );
                  })()
                ) : (
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                )}
                {msg.role === 'assistant' && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => {
                        setFeedbackByMessage((prev) => ({ ...prev, [idx]: 'up' }));
                        analytics.track('copilot_response_feedback', {
                          context,
                          persona,
                          rating: 'up',
                          responseLength: msg.content.length,
                        });
                        const prompt = msg.prompt || (idx > 0 && messages[idx - 1]?.role === 'user' ? messages[idx - 1].content : '');
                        void submitQualityFeedback('up', surfaceForMode(msg.mode), msg.content, prompt, msg.latencyMs);
                        toast.success('Thanks for the feedback');
                      }}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors ${
                        feedbackByMessage[idx] === 'up'
                          ? 'border-green-600 bg-green-900/30 text-green-300'
                          : 'border-neutral-600 text-neutral-300 hover:bg-neutral-700'
                      }`}
                    >
                      <ThumbsUp className="w-3.5 h-3.5" />
                      Helpful
                    </button>
                    <button
                      onClick={() => {
                        setFeedbackByMessage((prev) => ({ ...prev, [idx]: 'down' }));
                        analytics.track('copilot_response_feedback', {
                          context,
                          persona,
                          rating: 'down',
                          responseLength: msg.content.length,
                        });
                        const prompt = msg.prompt || (idx > 0 && messages[idx - 1]?.role === 'user' ? messages[idx - 1].content : '');
                        void submitQualityFeedback('down', surfaceForMode(msg.mode), msg.content, prompt, msg.latencyMs);
                        toast.success('Feedback recorded');
                      }}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors ${
                        feedbackByMessage[idx] === 'down'
                          ? 'border-red-600 bg-red-900/30 text-red-300'
                          : 'border-neutral-600 text-neutral-300 hover:bg-neutral-700'
                      }`}
                    >
                      <ThumbsDown className="w-3.5 h-3.5" />
                      Needs work
                    </button>
                    <button
                      onClick={() => {
                        setOutcomeByMessage((prev) => ({ ...prev, [idx]: 'resolved' }));
                        const prompt = msg.prompt || (idx > 0 && messages[idx - 1]?.role === 'user' ? messages[idx - 1].content : '');
                        void submitOutcomeFeedback('resolved', surfaceForMode(msg.mode), msg.content, prompt, msg.latencyMs);
                        toast.success('Marked as resolved');
                      }}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors ${
                        outcomeByMessage[idx] === 'resolved'
                          ? 'border-emerald-500 bg-emerald-900/30 text-emerald-300'
                          : 'border-neutral-600 text-neutral-300 hover:bg-neutral-700'
                      }`}
                    >
                      Resolved
                    </button>
                    <button
                      onClick={() => {
                        setOutcomeByMessage((prev) => ({ ...prev, [idx]: 'improved' }));
                        const prompt = msg.prompt || (idx > 0 && messages[idx - 1]?.role === 'user' ? messages[idx - 1].content : '');
                        void submitOutcomeFeedback('improved', surfaceForMode(msg.mode), msg.content, prompt, msg.latencyMs);
                        toast.success('Marked as improved');
                      }}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors ${
                        outcomeByMessage[idx] === 'improved'
                          ? 'border-blue-500 bg-blue-900/30 text-blue-300'
                          : 'border-neutral-600 text-neutral-300 hover:bg-neutral-700'
                      }`}
                    >
                      Improved
                    </button>
                    <button
                      onClick={() => {
                        setOutcomeByMessage((prev) => ({ ...prev, [idx]: 'not_helpful' }));
                        const prompt = msg.prompt || (idx > 0 && messages[idx - 1]?.role === 'user' ? messages[idx - 1].content : '');
                        void submitOutcomeFeedback('not_helpful', surfaceForMode(msg.mode), msg.content, prompt, msg.latencyMs);
                        toast.success('Marked as not helpful');
                      }}
                      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border transition-colors ${
                        outcomeByMessage[idx] === 'not_helpful'
                          ? 'border-rose-500 bg-rose-900/30 text-rose-300'
                          : 'border-neutral-600 text-neutral-300 hover:bg-neutral-700'
                      }`}
                    >
                      Not helpful
                    </button>
                  </div>
                )}
              </div>
            ))}

            {(loading || displayContent || steps.length > 0) && (
              <div className="tension-glow rounded-lg border border-neutral-700 bg-neutral-800 p-3 space-y-3">
                <div className="flex items-center gap-2 text-xs text-neutral-400 uppercase tracking-wide">
                  <Brain className="w-3.5 h-3.5" />
                  Process
                </div>
                <div className="space-y-1.5">
                  {steps.map((step) => (
                    <div key={step.id} className="flex items-center justify-between text-sm">
                      <span className="text-neutral-200">{step.label}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        step.status === 'done'
                          ? 'bg-green-900/50 text-green-300'
                          : step.status === 'active'
                            ? 'bg-violet-900/50 text-violet-300'
                            : 'bg-neutral-700 text-neutral-300'
                      }`}>
                        {step.status}
                      </span>
                    </div>
                  ))}
                </div>
                {(loading || displayContent) && (
                  <div className="rounded-md bg-neutral-900 border border-neutral-700 p-3">
                    <div className="flex items-center gap-2 text-xs text-neutral-400 mb-2">
                      <PencilLine className="w-3.5 h-3.5" />
                      AI typing
                    </div>
                    <p className="text-sm text-neutral-200 whitespace-pre-wrap">{displayContent || '...'}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="p-4 border-t border-neutral-800 shrink-0">
            {copilotMode === 'atlas_pro' ? (
              <div className="mb-3 grid grid-cols-3 gap-2">
                {[
                  { id: 'coach', label: 'Coach' },
                  { id: 'planner', label: 'Planner' },
                  { id: 'crisis', label: 'Crisis Support' },
                ].map((mode) => (
                  <button
                    key={mode.id}
                    onClick={() => setPersona(mode.id as CopilotPersona)}
                    className={`rounded-md border px-2 py-1.5 text-xs transition-colors ${
                      persona === mode.id
                        ? 'border-violet-500 bg-violet-500/20 text-violet-200'
                        : 'border-neutral-700 bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                    }`}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            ) : null}
            <div className="flex gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={copilotMode === 'live_companion' ? 'Paste the exact text moment. I will return send-ready variants.' : 'Ask for guidance, scripts, or action plans...'}
                className="min-h-[56px] max-h-40 resize-none bg-neutral-800 border-neutral-700 text-white"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    runCopilot();
                  }
                }}
              />
              <Button onClick={runCopilot} disabled={!canSend} className="h-14 bg-violet-600 hover:bg-violet-700">
                <Send className="w-4 h-4" />
              </Button>
            </div>
            {messages.length > 0 && (
              <div className="mt-2">
                <button
                  onClick={() => {
                    setMessages([]);
                    setDisplayContent('');
                    setSteps([]);
                    toast.success('Copilot session cleared');
                  }}
                  className="text-[11px] text-neutral-500 hover:text-neutral-300 transition-colors"
                >
                  Clear session conversation
                </button>
              </div>
            )}
            <div className="mt-2 flex items-center gap-3 text-[11px] text-neutral-500">
              <span className="inline-flex items-center gap-1"><Brain className="w-3 h-3" /> reasoning stage</span>
              <span className="inline-flex items-center gap-1"><Search className="w-3 h-3" /> context stage</span>
              <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> drafted output</span>
            </div>
            {copilotMode === 'atlas_pro' && persona === 'crisis' && (
              <p className="mt-2 text-[11px] text-red-300">
                Crisis mode is guidance only and not a substitute for emergency or clinical services.
              </p>
            )}
          </div>
          </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
